import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { customersAPI, adminAPI, usersAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Plus, Search, Building2, ChevronRight, Loader2, AlertCircle, PowerOff, Power, Trash2, Shield, Paperclip } from 'lucide-react';
import { CountrySelect } from '../components/CountrySelect';
import { toast } from 'sonner';

const COUNTRIES = ['United Kingdom', 'Germany', 'France', 'Netherlands', 'Spain', 'Italy', 'United States', 'Canada', 'Australia', 'UAE', 'South Africa', 'Other'];
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const PARTNER_TYPES = ['SAP Partner', 'VAR', 'ISV', 'MSP', 'System Integrator', 'Reseller', 'Distributor', 'Other'];

const defaultForm = {
  company_name: '', partner_type: 'SAP Partner', website: '', billing_address: '',
  country: 'United Kingdom', default_currency: 'GBP',
  default_region_id: '', default_region_name: '',
  default_psm_id: '', default_psm_name: '',
  notes: '', tags: [],
};

export default function CustomersPage() {
  const { isManager, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [psmUsers, setPsmUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sortBy, setSortBy] = useState('company_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const PAGE_SIZE = 20;

  const canManage = isAdmin || isManager;

  // Load regions and users once on mount
  useEffect(() => {
    adminAPI.getRegions().then(r => setRegions(r.data || [])).catch(() => {});
    usersAPI.list().then(r => setPsmUsers((Array.isArray(r.data) ? r.data : []).filter(u => u.is_active !== false))).catch(() => {});
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); }
    else { setSortBy(field); setSortOrder('asc'); }
    setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, status: statusFilter, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      const { data } = await customersAPI.list(params);
      setCustomers(data.items || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load partners'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault(); setErr(null); setSaving(true);
    try {
      await customersAPI.create(form);
      toast.success(`${form.company_name} created`);
      setShowCreate(false); setForm(defaultForm); load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed to create partner'); }
    finally { setSaving(false); }
  };

  const handleFormRegionChange = (regionId) => {
    const r = regions.find(r => r.id === regionId);
    setForm(f => ({
      ...f,
      default_region_id:   regionId || null,
      default_region_name: r?.name || null,
      default_currency:    r?.default_currency || f.default_currency,
    }));
  };

  const quickToggleStatus = async (e, c) => {
    e.stopPropagation();
    const newStatus = c.status === 'active' ? 'inactive' : 'active';
    try {
      await customersAPI.update(c.id, { status: newStatus });
      toast.success(`${c.company_name} marked as ${newStatus}`);
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customersAPI.delete(deleteTarget.id);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch { toast.error('Failed to delete partner'); }
    finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}>Partners</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            {isManager ? 'All team partners' : 'Your partners'} · {total} total
          </p>
        </div>
        <Button onClick={() => navigate('/partners/new')} style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} className="gap-2" data-testid="customers-new-button">
          <Plus size={15} /> New Partner
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--muted-fg))' }} />
          <Input placeholder="Search by company or country..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9" data-testid="customers-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All Partners</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              {[['company_name','Company'],['country','Country'],['default_currency','Currency']].map(([f,l]) => {
                const active = sortBy === f;
                return (
                  <TableHead key={f} className="cursor-pointer select-none hover:bg-muted/30"
                    onClick={() => handleSort(f)} data-testid={`sort-${f}`}>
                    <div className="flex items-center gap-1">{l}
                      {active ? (sortOrder === 'desc' ? <span style={{ fontSize:10,opacity:.7 }}>↓</span> : <span style={{ fontSize:10,opacity:.7 }}>↑</span>) : <span style={{ fontSize:10,opacity:.3 }}>↕</span>}
                    </div>
                  </TableHead>
                );
              })}
              <TableHead>Website</TableHead>
              <TableHead>Partner Type</TableHead>
              <TableHead>Contacts</TableHead>
              <TableHead className="text-center">MSA</TableHead>
              <TableHead className="text-center">Doc</TableHead>
              <TableHead>Status</TableHead>
              {canManage && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
            )) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-12">
                  <Building2 size={28} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
                  <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No partners found. Create one to get started.</p>
                </TableCell>
              </TableRow>
            ) : customers.map(c => (
              <TableRow key={c.id} className="data-table-row cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                <TableCell>
                  <div className="font-medium">{c.company_name}</div>
                </TableCell>
                <TableCell className="text-sm">{c.country || '—'}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs font-mono">{c.default_currency}</Badge></TableCell>
                <TableCell className="text-xs max-w-[160px] truncate" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {c.website || '\u2014'}
                </TableCell>
                <TableCell className="text-sm">{c.partner_type}</TableCell>
                <TableCell className="text-sm">{(c.contacts || []).length}</TableCell>
                <TableCell className="text-center">
                  {c.msa_signed ? (
                    <span title="MSA recorded as signed" data-testid={`msa-signed-indicator-${c.id}`}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                      style={{ background: '#dcfce7' }}>
                      <Shield size={12} style={{ color: '#15803d' }} />
                    </span>
                  ) : (
                    <span title="MSA not signed" data-testid={`msa-unsigned-indicator-${c.id}`}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                      style={{ background: '#fef2f2' }}>
                      <Shield size={12} style={{ color: '#dc2626' }} />
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {c.msa_document ? (
                    <span title={c.msa_document.filename} data-testid={`msa-doc-indicator-${c.id}`}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                      style={{ background: '#f0fdf4' }}>
                      <Paperclip size={12} style={{ color: '#15803d' }} />
                    </span>
                  ) : (
                    <span className="text-sm" style={{ color: 'hsl(var(--border))' }}>&mdash;</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className="text-xs font-medium"
                    style={c.status === 'active'
                      ? { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' }
                      : { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' }
                    }
                  >
                    {c.status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                {canManage && (
                  <TableCell>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Button
                        variant="ghost" size="sm"
                        title={c.status === 'active' ? 'Deactivate' : 'Activate'}
                        onClick={e => quickToggleStatus(e, c)}
                        style={{ color: c.status === 'active' ? '#d97706' : '#15803d' }}
                        data-testid={`toggle-status-${c.id}`}
                      >
                        {c.status === 'active' ? <PowerOff size={13} /> : <Power size={13} />}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        title="Delete partner"
                        onClick={e => { e.stopPropagation(); setDeleteTarget({ id: c.id, name: c.company_name }); }}
                        style={{ color: 'hsl(var(--danger))' }}
                        data-testid={`delete-partner-${c.id}`}
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: 'hsl(var(--muted-fg))' }}>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Partner</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {err && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}
            <div className="space-y-1">
              <Label>Company Name *</Label>
              <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required data-testid="customer-company-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Partner Type</Label>
                <Select value={form.partner_type} onValueChange={v => setForm(f => ({ ...f, partner_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Website</Label>
                <Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Country</Label>
                <CountrySelect value={form.country} onChange={v => setForm(f => ({ ...f, country: v }))} testId="create-country" />
              </div>
              <div className="space-y-1">
                <Label>Default Currency</Label>
                <Select value={form.default_currency} onValueChange={v => setForm(f => ({ ...f, default_currency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Default AWS Region</Label>
              <Select value={form.default_region_id || 'none'} onValueChange={v => handleFormRegionChange(v === 'none' ? null : v)}>
                <SelectTrigger data-testid="create-region-select"><SelectValue placeholder="No default region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No default region</SelectItem>
                  {regions.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name} · {r.aws_region_code} · {r.default_currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                Auto-sets region and currency when this partner is selected in a new quote.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Default PSM</Label>
              <Select value={form.default_psm_id || 'none'} onValueChange={v => {
                const u = psmUsers.find(u => u.id === v);
                setForm(f => ({ ...f, default_psm_id: v === 'none' ? '' : v, default_psm_name: u?.name || '' }));
              }}>
                <SelectTrigger><SelectValue placeholder="No default PSM" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No default PSM</SelectItem>
                  {psmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Billing Address</Label>
              <Input value={form.billing_address} onChange={e => setForm(f => ({ ...f, billing_address: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm h-20 resize-none" placeholder="Internal notes about this partner..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }} data-testid="customer-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Partner'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={18} style={{ color: 'hsl(var(--danger))' }} />
              Delete Partner
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            This action cannot be undone. Existing quotes will not be affected.
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}
              data-testid="confirm-delete-partner-list">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
